import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	forwardRef
} from '@angular/core';

import { isEqual } from 'underscore';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { VgStates } from './videogular/VgStates';
import { VideogularComponent } from './videogular/Videogular.Component';

import { IVbPlaybackTokenHandlerCreate } from './IVbPlaybackTokenHandlerCreate';
import { IVbSource } from './IVbSource';
import { NativeCrossOrigin } from './CrossOriginType';
import { PlaybackOption } from './PlaybackOption';
import { VbPlaybackTokenHandler, VbPlaybackToken } from './VbPlaybackToken.Provider';
import { SupportedPlaybacksService } from './SupportedPlaybacks.Service';

enum ErrorEventType {
	MULTICAST = 'multicastError',
	PLAYBACK = 'playbackError'
}

export interface IOnMediaFailoverEvent {
	fromUrl: string;
	isToFlash: boolean;
	playback: IVbSource;
	toUrl: string;
}

export interface IPlaybackErrorEvent {
	data: any;
	isFatal: boolean;
}

export interface IPlaybackChangeEvent {
	isFlash: boolean;
	playback: PlaybackOption;
	src: string;
}

@Component({
	selector: 'vb-playback-manager',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: ``,
	standalone: false
})
export class VbPlaybackManagerComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public currentState: VgStates;
	@Input() public playbackOptions: IVbSource[];
	@Input() public sources: PlaybackOption[];

	@Output() public onCrossOriginError = new EventEmitter<void>();
	@Output() public onInitialPlayback = new EventEmitter<IPlaybackChangeEvent>();
	@Output() public onManualStreamSwitch = new EventEmitter<IPlaybackChangeEvent>();
	@Output() public onMediaFailover = new EventEmitter<IOnMediaFailoverEvent>();
	@Output() public onMulticastError = new EventEmitter<IPlaybackErrorEvent>();
	@Output() public onPlaybackError = new EventEmitter<IPlaybackErrorEvent>();
	@Output() public onCrossOriginSet = new EventEmitter<NativeCrossOrigin>();

	private isDestroyed: boolean;
	private onCurrentStateChangeOneTime: () => void;
	private onSourcesChangeOneTime: () => void;
	private vbPlaybackTokenHandler: VbPlaybackTokenHandler;
	public supportedPlaybackOptions: PlaybackOption[]; // will deprecate in future. don't rely on it. check context$.

	private supportedPlaybackOptionsSubject$: BehaviorSubject<IVbSource[]> = new BehaviorSubject<IVbSource[]>([]);
	public context$: Observable<any> = this.supportedPlaybackOptionsSubject$.asObservable()
		.pipe(
			map(supportedPlaybackOptions => ({ supportedPlaybackOptions }))
		);

	constructor(
		private SupportedPlaybacks: SupportedPlaybacksService,
		@Inject(VbPlaybackToken) private vbPlaybackToken: IVbPlaybackTokenHandlerCreate,
		private vgApi: VideogularComponent
	) {}

	public ngOnInit(): void {
		this.supportedPlaybackOptions = this.SupportedPlaybacks.getSupportedPlaybackOptions(this.playbackOptions, this.vgApi.flashDisabled, this.vgApi.forceFlashHlsPlayback);
		this.supportedPlaybackOptionsSubject$.next(this.supportedPlaybackOptions);
	}

	public ngOnDestroy(): void {
		this.isDestroyed = true;
		this.resetTokenHandler();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.sources && this.onSourcesChangeOneTime && this.sources && !this.sources.length) {
			this.onSourcesChangeOneTime();
			this.onSourcesChangeOneTime = null;
		}

		if (changes.playbackOptions) {
			this.onPlaybackOptionsChange(changes.playbackOptions.currentValue, changes.playbackOptions.previousValue);
		}

		if (changes.currentState && this.onCurrentStateChangeOneTime && this.currentState === VgStates.PLAY) {
			this.onCurrentStateChangeOneTime();
			this.onCurrentStateChangeOneTime = null;
		}
	}

	private fallbackToNextPlaybackOption(): void {
		const nextPlaybackOption: PlaybackOption = this.getNextPlaybackOption(this.vgApi.activeSource);

		if (nextPlaybackOption) {
			console.info('Multicast playback error - falling back to the next playback option.');

			this.updateSelection(nextPlaybackOption);

			this.raiseFailoverEvent(this.vgApi.activeSource.src, nextPlaybackOption.src, nextPlaybackOption.type, nextPlaybackOption);
		} else {
			console.info('Multicast playback error - no fallbacks available.');

			this.unloadPlaybackPlugin();
		}
	}

	private getNextPlaybackOption(currentPlaybackOption: PlaybackOption): PlaybackOption {
		const playbackOptions: PlaybackOption[] = this.supportedPlaybackOptions;
		const currentPlaybackOptionIndex: number = playbackOptions.findIndex(playbackOption => playbackOption.label === currentPlaybackOption.label);
		const nextPlaybackOptionIndex: number = currentPlaybackOptionIndex + 1;

		return nextPlaybackOptionIndex < playbackOptions.length ? playbackOptions[nextPlaybackOptionIndex] : null;
	}

	private onPlaybackOptionsChange(newValue?: IVbSource, oldValue?: IVbSource): void {
		//short-circuit, if there's no change
		if (newValue && isEqual(newValue, oldValue)) {
			return;
		}

		this.supportedPlaybackOptions = this.SupportedPlaybacks.getSupportedPlaybackOptions(this.playbackOptions, this.vgApi.flashDisabled, this.vgApi.forceFlashHlsPlayback);
		this.supportedPlaybackOptionsSubject$.next(this.supportedPlaybackOptions);

		this.vgApi.isSupportedPlaybackOptions = !!this.supportedPlaybackOptions.length;

		if (this.vgApi.isSupportedPlaybackOptions) {
			const initialPlaybackOption =
				this.supportedPlaybackOptions.find(option => option.isDefault) ||
				this.supportedPlaybackOptions[0];

			this.updateSelection(initialPlaybackOption, true);

			this.onInitialPlayback.emit({
				src: initialPlaybackOption.src,
				isFlash: this.SupportedPlaybacks.getIsFlashPlayback(initialPlaybackOption, this.vgApi.flashDisabled, this.vgApi),
				playback: initialPlaybackOption
			});
		} else if (this.vgApi.activeSource) {
			this.vgApi.sources = [];
			this.vgApi.activeSource = new PlaybackOption({} as any);
		}
	}

	private raiseErrorEvent(type: string, error: any): void {
		switch(type) {
			case ErrorEventType.MULTICAST:
				this.onMulticastError.emit({
					data: error,
					isFatal: this.getIsFatalError(true)
				});
				break;

			case ErrorEventType.PLAYBACK:
				this.onPlaybackError.emit({
					data: error,
					isFatal: this.getIsFatalError(false)
				});
				break;
		}
	}

	private raiseFailoverEvent(fromUrl: string, toUrl: string, toType: string, toPlayback: IVbSource, isToFlash: boolean = this.SupportedPlaybacks.getIsFlashPlayback({ src: toUrl, type: toType } as IVbSource, this.vgApi.flashDisabled, this.vgApi)): void {
		this.onMediaFailover.emit({
			fromUrl,
			isToFlash,
			playback: toPlayback,
			toUrl
		});
	}

	public updateSelection(playbackOption: PlaybackOption, isPlaybackOptionsChange: boolean = false): void {
		window.setTimeout(() => {
			const autoPlayConfig: boolean = this.vgApi.autoPlay;
			const isPlaying = this.vgApi.isLive ?
				this.vgApi.currentState !== VgStates.STOP : // may get thrown into PAUSE during live resume behavior
				this.vgApi.currentState === VgStates.PLAY;

			if (!isPlaybackOptionsChange) {
				if (isPlaying) {
					this.vgApi.pause();

					//use autoPlay use that playback gets restarted right away
					this.vgApi.autoPlay = true;

					//reset autoPlay after playback starts again
					this.onCurrentStateChangeOneTime = () => {
						this.vgApi.autoPlay = autoPlayConfig;
					};
				}

				//seek to the current time in the new source (required a Videogular fix to get this working since it wasn't resetting the hasStartTimePlayed flag)
				if (this.vgApi.activeSource && !this.vgApi.activeSource.isLive) {
					this.vgApi.onUpdateStartTime(this.vgApi.currentTime / 1000, true); //ms to sec
				}
			} else if (isPlaying && !this.vgApi.autoPlay) { //playbackOptions change AND is currently playing
				this.vgApi.pause(); //workaround some browsers leaving videogular stuck in the playing state after a source change even though it's not playing
			}

			this.onCrossOriginSet.emit(this.setNativeCrossOrigin(playbackOption));

			this.shapePlaybackOption(playbackOption)
				.then(playbackResponse => this.vgApi.sources = [playbackResponse]);
		});
	}

	private setNativeCrossOrigin(playbackOption: IVbSource): NativeCrossOrigin {
		if(playbackOption.src.startsWith(location.origin)) {
			return NativeCrossOrigin.USE_CREDENTIALS;
		}

		return playbackOption.tokenConfig && playbackOption.tokenConfig.withCredentials
			? NativeCrossOrigin.USE_CREDENTIALS
			: NativeCrossOrigin.ANONYMOUS;
	}

	private shapePlaybackOption(playbackOption): Promise<IVbSource> {
		this.resetTokenHandler();

		if (!playbackOption.tokenConfig) {
			return Promise.resolve(playbackOption);
		}

		this.vbPlaybackTokenHandler = this.vbPlaybackToken(playbackOption);

		return this.vbPlaybackTokenHandler.fetchFreshToken();
	}

	private resetTokenHandler(): void {
		if (this.vbPlaybackTokenHandler) {
			this.vbPlaybackTokenHandler.cancelFetchToken();
			this.vbPlaybackTokenHandler.cancelRefreshInterval();
			this.vbPlaybackTokenHandler = undefined;
		}
	}

	private unloadPlaybackPlugin(): void {
		const { playbackPluginUnload } = this.vgApi;

		if (playbackPluginUnload) {
			playbackPluginUnload();
		}
	}

	public hlsCrossOriginError(): void {
		console.info('hlsCrossOriginError');

		if (this.isDestroyed) {
			return;
		}

		// if 360 playback OR Flash is disabled OR Flash is not supported, fire a regular mediaError instead since we don't want to try Flash
		if (this.SupportedPlaybacks.isFlashHlsPlaybackUnsupported(this.vgApi.activeSource, this.vgApi.flashDisabled)) {
			this.mediaError({});
			return;
		}

		//if hls.js is used and a cross-origin error occurs, fallback to Flash playback
		this.vgApi.forceFlashHlsPlayback = true;

		const { activeSource } = this.vgApi;

		this.onSourcesChangeOneTime = () => {
			console.warn('Falling back to HLS playback through Flash');

			this.updateSelection(activeSource);
			this.onCrossOriginError.emit();
			this.raiseFailoverEvent(activeSource.src, activeSource.src, activeSource.type, activeSource, true);
		};

		//clear out the sources, so that reapplying the same sources gets recognized as a change
		this.vgApi.sources = [];
	}

	public flashMulticastPlaybackError(): void {
		this.vgApi.activeSource.isUnavailable = true;

		//if flash multicast playback fails, fallback to either the next src option for the playback or to the next playback option in line
		if (this.vgApi.activeSource.srcOptions.length) { //use fallback within the activeSource
			console.info('Multicast playback error - falling back to the next source for this playback option.');

			this.vgApi.onVideoError();
		} else { //fall to next playback option (if any)
			this.raiseErrorEvent(ErrorEventType.MULTICAST, 'BufferEmptyAfterConnection');

			this.fallbackToNextPlaybackOption();
		}
	}

	public mediaError(event, origEvent?): void { // TODO: test origEvent (Doesn't look to have been passed)
		if (!this.vgApi.activeSource || this.isDestroyed) {
			return;
		}

		//flash not used any more. clean up later.
		if (event && event.type === 'flashMulticastPlaybackError') {
			this.flashMulticastPlaybackError();
			return;
		}

		const currentSrc: string = this.vgApi.activeSource.src || '';
		const currentSrcType: string = this.vgApi.activeSource.type || '';
		const activeSource: PlaybackOption = this.vgApi.activeSource;

		activeSource.isUnavailable = true;

		//ignore errors coming from a native video element that is not in the DOM (may be removed by the flash plugin)
		if (origEvent) {
			const videoElement: HTMLVideoElement = origEvent.srcElement ? origEvent.srcElement : origEvent.target;

			if (videoElement && (!videoElement.parentNode || this.vgApi.ignoreDomErrors)) {
				return;
			}
		}

		//fire the error event.. for multicast, fire specific event...
		const isMulticast: boolean = this.SupportedPlaybacks.isMulticastSource(currentSrc, currentSrcType);

		this.raiseErrorEvent(isMulticast ? ErrorEventType.MULTICAST : ErrorEventType.PLAYBACK, { event, origEvent });

		//fallback to the next src option, if any available, for the current playback option
		if (activeSource.srcOptions.length) {
			//once the sources clearing has gone through, go ahead and apply the fallback
			this.onSourcesChangeOneTime = () => {
				activeSource.fallback();

				console.warn(`Falling back from the ${activeSource.label} playback option ${currentSrc} to ${activeSource.src}`);

				this.updateSelection(activeSource);
				this.raiseFailoverEvent(currentSrc, activeSource.src, activeSource.type, activeSource);
			};

			//clear out the sources to get videogular in a happy state again
			this.vgApi.sources = [];
		} else if (isMulticast) {
			this.fallbackToNextPlaybackOption();
		} else {
			window.setTimeout(() => { // timeout improves reliability
				if (this.vgApi.currentState === VgStates.PLAY) {
					if (activeSource.isLive) {
						this.vgApi.stop();
					} else {
						this.vgApi.pause();
					}
				}

				this.unloadPlaybackPlugin();
			});
		}
	}

	private getIsFatalError(allowFallbackToNextOption: boolean): boolean {
		const { activeSource } = this.vgApi;

		const isFallback: boolean = activeSource.srcOptions.length > 0 ||
			(
				allowFallbackToNextOption &&
				!!this.getNextPlaybackOption(activeSource)
			);

		return !isFallback;
	}
}
